import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { useTitle } from '../../helpers/use_title.helper'
import Footer from '../../components/Footer/Footer'
import kiLogo from '../../assets/ki-footer-white.svg'
import './Legal.css'

const TOC_ITEMS = [
  { id: 's1', label: '1. Introducción y alcance' },
  { id: 's2', label: '2. Marco legal' },
  { id: 's3', label: '3. Definiciones' },
  { id: 's4', label: '4. Rol de Ki como Encargado' },
  { id: 's5', label: '5. Datos personales tratados' },
  { id: 's6', label: '6. Finalidades del tratamiento' },
  { id: 's7', label: '7. WhatsApp Business Platform' },
  { id: 's8', label: '8. Sub-encargados y transferencias' },
  { id: 's9', label: '9. Seguridad de la información' },
  { id: 's10', label: '10. Retención de datos' },
  { id: 's11', label: '11. Derechos de los Titulares' },
  { id: 's12', label: '12. Datos sensibles de salud' },
  { id: 's13', label: '13. Menores de edad' },
  { id: 's14', label: '14. Cookies' },
  { id: 's15', label: '15. Modificaciones' },
  { id: 's16', label: '16. Contacto' },
  { id: 's17', label: '17. Vigencia' }
]

function Legal () {
  useTitle('Ki | Política de Privacidad')

  const [activeId, setActiveId] = useState('s1')
  const observerRef = useRef(null)

  useEffect(() => {
    const sections = document.querySelectorAll('.legal-section')
    /* eslint-disable-next-line no-undef */
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id)
        })
      },
      { rootMargin: '-20% 0px -70% 0px' }
    )
    sections.forEach((s) => observerRef.current.observe(s))
    return () => observerRef.current?.disconnect()
  }, [])

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className='legal-page'>

      {/* Header */}
      <header className='legal-header'>
        <Link to='/'>
          <img src={kiLogo} alt='Ki' />
        </Link>

        {/* Hero */}
        <div className='legal-hero'>
          <span className='legal-product-tag'>Ki Conversations</span>
          <h1>Política de Privacidad y<br />Tratamiento de Datos Personales</h1>
          <p className='legal-update-date'>Última actualización: 23 de abril de 2026</p>
          <p className='legal-warning'>
            POR FAVOR LEA CUIDADOSAMENTE ESTA POLÍTICA DE PRIVACIDAD. CONTIENE INFORMACIÓN IMPORTANTE
            SOBRE CÓMO KI CONVERSATIONS (KI PLANNER S.A.S) TRATA LOS DATOS PERSONALES DE PACIENTES
            POR CUENTA DE CLÍNICAS Y PROFESIONALES DE LA SALUD QUE CONTRATAN NUESTROS SERVICIOS.
          </p>
        </div>
      </header>

      {/* Body: ToC + Content */}
      <div className='legal-body'>

        {/* Sidebar */}
        <nav className='legal-toc'>
          <h3>Contenido</h3>
          <ul>
            {TOC_ITEMS.map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className={activeId === item.id ? 'active' : ''}
                  onClick={(e) => { e.preventDefault(); scrollTo(item.id) }}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Content */}
        <main className='legal-content'>

          {/* 1 */}
          <section className='legal-section' id='s1'>
            <h2>1. Introducción y alcance</h2>
            <p>
              Ki Conversations es un producto operado por Ki Planner S.A.S. (en adelante, "Ki" o "la Compañía"),
              sociedad colombiana identificada con NIT 901753233-9, con domicilio en Carrera 17 No. 2 sur – 10,
              Medellín, Antioquia, República de Colombia.
            </p>
            <p>
              Ki Conversations es una plataforma de software que permite a clínicas, consultorios y profesionales
              de la salud automatizar la confirmación de citas, recordatorios y comunicaciones operativas con sus
              pacientes a través del canal WhatsApp Business. En el marco de este servicio, Ki procesa datos
              personales de pacientes exclusivamente por cuenta e instrucción de cada clínica cliente.
            </p>
            <p>
              Esta Política aplica específicamente a Ki Conversations y es independiente de la política de
              privacidad del producto Ki Medicamentos (comparador de precios y adquisición de medicamentos),
              disponible en{' '}
              <a href='https://cliente.kiplanner.com/legal' target='_blank' rel='noreferrer'>
                https://cliente.kiplanner.com/legal
              </a> o en
              <a href='https://www.kiplanner.com/legal/conversations/politica-de-privacidad' target='_blank' rel='noreferrer'>
                https://www.kiplanner.com/legal/conversations/politica-de-privacidad
              </a>.
            </p>
            <p>
              Al utilizar Ki Conversations — ya sea como clínica contratante, coordinador o usuario administrativo
              — se reconoce que esta Política constituye un marco vinculante para el tratamiento de datos
              personales dentro de la plataforma y en este sentido, acepto su contenido y alcance.
            </p>
          </section>
          <hr className='legal-divider' />

          {/* 2 */}
          <section className='legal-section' id='s2'>
            <h2>2. Marco legal aplicable</h2>
            <p>Ki Conversations cumple con la normatividad colombiana vigente en materia de protección de datos personales, incluyendo:</p>
            <ul>
              <li>La Constitución Política de Colombia de 1991, artículos 15 y 20.</li>
              <li>La Ley 1581 de 2012, por la cual se dictan disposiciones generales para la protección de datos personales.</li>
              <li>Los Decretos 1377 de 2013, 886 de 2014 y 1074 de 2015, y demás normas que los modifiquen, adicionen o sustituyan.</li>
              <li>Las disposiciones sectoriales aplicables al manejo de información de salud, incluyendo la Ley 1266 de 2008 y la Ley Estatutaria 1751 de 2015.</li>
            </ul>
          </section>
          <hr className='legal-divider' />

          {/* 3 */}
          <section className='legal-section' id='s3'>
            <h2>3. Definiciones</h2>
            <p>Para efectos de esta Política, se entiende por:</p>
            <ul>
              <li><strong>Titular:</strong> La persona natural cuyos datos personales son objeto de tratamiento. En el contexto de Ki Conversations, los Titulares son principalmente los pacientes de las clínicas clientes, y en menor medida el personal administrativo de dichas clínicas.</li>
              <li><strong>Responsable del Tratamiento:</strong> La persona natural o jurídica que decide sobre la base de datos y/o el tratamiento de los datos. En Ki Conversations, el Responsable es cada clínica cliente respecto de los datos de sus pacientes.</li>
              <li><strong>Encargado del Tratamiento:</strong> La persona natural o jurídica que realiza el tratamiento de datos personales por cuenta del Responsable. Ki actúa como Encargado del Tratamiento frente a los datos de pacientes de las clínicas clientes.</li>
              <li><strong>Sub-encargado:</strong> Un tercero al cual Ki, como Encargado, delega total o parcialmente el tratamiento de datos (por ejemplo, proveedores de infraestructura tecnológica).</li>
              <li><strong>Clínica cliente:</strong> Clínica, consultorio, profesional de la salud o entidad prestadora de servicios de salud que contrata los servicios de Ki Conversations y que actúa como Responsable del Tratamiento.</li>
              <li><strong>Paciente:</strong> Persona natural usuaria de los servicios de salud de una clínica cliente, cuyos datos son tratados por Ki por cuenta de dicha clínica.</li>
              <li><strong>Coordinador:</strong> Personal administrativo de una clínica cliente (secretarias, agentes de call center, gerentes) que utiliza la plataforma Ki Conversations para gestionar las comunicaciones con pacientes.</li>
              <li><strong>Datos Sensibles:</strong> Aquellos relacionados con el origen racial o étnico, la pertenencia a sindicatos, organizaciones sociales o de derechos humanos, convicciones políticas, religiosas, de la vida sexual, biométricos o datos de la salud.</li>
              <li><strong>WhatsApp Business Platform:</strong> Plataforma de mensajería empresarial operada por Meta Platforms, Inc., utilizada como canal de comunicación entre las clínicas clientes y sus pacientes.</li>
            </ul>
          </section>
          <hr className='legal-divider' />

          {/* 4 */}
          <section className='legal-section' id='s4'>
            <h2>4. Rol de Ki como Encargado del Tratamiento</h2>
            <p>
              En Ki Conversations, cada clínica cliente actúa como Responsable del Tratamiento de los datos de sus
              pacientes. Ki actúa exclusivamente como Encargado del Tratamiento, procesando dichos datos siguiendo
              las instrucciones de la clínica cliente y únicamente para las finalidades contratadas.
            </p>
            <p>En su calidad de Encargado, Ki asume las siguientes obligaciones:</p>
            <ul>
              <li>Tratar los datos personales de pacientes únicamente conforme a las instrucciones de la clínica cliente.</li>
              <li>Mantener la confidencialidad, integridad y disponibilidad de los datos mediante medidas técnicas y organizativas apropiadas.</li>
              <li>No utilizar los datos de pacientes para finalidades distintas a la prestación del servicio contratado por la clínica.</li>
              <li>No ceder, transferir ni comercializar datos de pacientes a terceros, salvo los sub-encargados debidamente identificados en esta Política.</li>
              <li>Colaborar con la clínica cliente en la atención de solicitudes de derechos de los Titulares.</li>
              <li>Notificar a la clínica cliente cualquier incidente de seguridad que comprometa datos personales de sus pacientes.</li>
              <li>Devolver o suprimir los datos al finalizar la relación contractual con la clínica, según lo establecido en la sección de retención.</li>
            </ul>
            <div className='legal-callout'>
              Las clínicas clientes, como Responsables del Tratamiento, conservan la responsabilidad primaria
              frente a sus pacientes por el cumplimiento de los principios y derechos previstos en la Ley 1581 de 2012.
            </div>
          </section>
          <hr className='legal-divider' />

          {/* 5 */}
          <section className='legal-section' id='s5'>
            <h2>5. Datos personales tratados</h2>

            <h3>5.1. Datos de pacientes</h3>
            <p>
              Por cuenta de cada clínica cliente, Ki recolecta y trata las siguientes categorías de datos
              personales de pacientes, cada una con una finalidad específica y legítima necesaria para la
              prestación del servicio:
            </p>

            <h3>5.1.1. Datos de identificación y contacto</h3>
            <ul>
              <li>Nombres y apellidos del paciente.</li>
              <li>Número de teléfono móvil asociado a WhatsApp.</li>
              <li>Tipo y número de documento de identidad, cuando la clínica cliente lo proporciona. Esta información se utiliza exclusivamente para identificar inequívocamente al paciente dentro de los sistemas de agendamiento de la clínica y recuperar los datos de contacto necesarios para el envío de la confirmación.</li>
              <li>Cuando el paciente es menor de edad o requiere acudiente, información básica del acudiente provista por la clínica.</li>
            </ul>

            <h3>5.1.2. Datos de la cita y la comunicación</h3>
            <ul>
              <li>Fecha y hora de la cita programada.</li>
              <li>Sede o ubicación donde se atenderá la cita.</li>
              <li>Especialidad, tipo de procedimiento y categoría de cita (por ejemplo: odontología, fisioterapia, consulta dermatológica).</li>
              <li>Nombre del profesional de salud asignado.</li>
              <li>Fecha de la primera cita del paciente con la clínica.</li>
              <li>Indicadores operativos relacionados con la cita, cuando aplican (por ejemplo: si requiere contacto previo, preparación con laboratorios o radiografías, uso de implantes).</li>
            </ul>

            <h3>5.1.3. Datos de personalización del mensaje</h3>
            <ul>
              <li>Proveedor de seguro o asegurador del paciente (EPS, medicina prepagada, pólizas, etc.), utilizado exclusivamente para personalizar el contenido de los mensajes de confirmación.</li>
            </ul>

            <h3>5.1.4. Datos de mensajería y operación</h3>
            <ul>
              <li>Contenido y metadatos de los mensajes de WhatsApp enviados y recibidos (texto del mensaje, identificadores técnicos de conversación y mensaje, marcas de tiempo, estados de entrega, lectura y respuesta).</li>
              <li>Respuestas textuales recibidas del paciente por WhatsApp.</li>
              <li>Historial de estados de la cita: programada, enviada, entregada, vista, confirmada, cancelada, reprogramada o ignorada.</li>
              <li>Inferencias generadas por sistemas automatizados de clasificación (por ejemplo, la intención detectada en la respuesta del paciente y el nivel de confianza asociado), utilizadas para actualizar el estado de la cita y priorizar la atención por parte del coordinador.</li>
            </ul>

            <h3>5.1.5. Metadatos técnicos</h3>
            <ul>
              <li>Identificador de la clínica cliente y de los profesionales asociados, para el enrutamiento correcto de los datos en la arquitectura multicliente de Ki.</li>
              <li>Identificadores internos y marcas de tiempo de creación y actualización de los registros.</li>
              <li>Trazas de error técnicas generadas cuando ocurren incidentes en el procesamiento (mensaje, etapa, marca de tiempo). Ki implementa controles para evitar la inclusión de datos identificables del paciente en dichas trazas.</li>
            </ul>

            <div className='legal-callout'>
              <strong>Datos que Ki no recolecta ni procesa en Ki Conversations.</strong> Con base en el principio
              de minimización de datos establecido en la Ley 1581 de 2012, Ki Conversations no recolecta, almacena
              ni procesa los siguientes datos de pacientes: correo electrónico, dirección física, país, departamento,
              ciudad, género, estado civil, fecha de nacimiento, lugar de nacimiento ni tipo de sangre. Si en el
              futuro Ki desarrolla funcionalidades que requieran alguno de estos datos, esta Política será
              actualizada previamente y comunicada a las clínicas clientes antes de iniciar dicho tratamiento.
            </div>

            <div className='legal-callout-warning'>
              <strong>Datos clínicos excluidos.</strong> Ki no accede, almacena ni procesa historia clínica,
              diagnósticos médicos, resultados de exámenes, prescripciones, información genética, datos
              biométricos, ni información clínica detallada de los pacientes. Los datos procesados se limitan
              estrictamente a la logística de citas, la personalización operativa del mensaje y la comunicación
              necesaria para confirmar, recordar o reprogramar dichas citas.
            </div>

            <h3>5.2. Datos de coordinadores y personal administrativo</h3>
            <p>Respecto al personal administrativo de las clínicas clientes que utiliza la plataforma (coordinadores, agentes de call center, secretarias), Ki trata:</p>
            <ul>
              <li>Nombres y apellidos.</li>
              <li>Correo electrónico corporativo.</li>
              <li>Número de teléfono, cuando se registra.</li>
              <li>Contraseña, almacenada exclusivamente mediante hash criptográfico (nunca en texto plano).</li>
              <li>Para perfiles con verificación adicional (por ejemplo, usuarios con permisos elevados), información técnica de verificación: estado, código temporal, marca de tiempo y número de intentos.</li>
              <li>Rol y estado del usuario dentro de la plataforma.</li>
              <li>Sedes o ubicaciones asignadas que definen el alcance de acceso del usuario.</li>
              <li>Clínica cliente a la que pertenece.</li>
              <li>Identificadores internos y marcas de tiempo de creación y actualización de la cuenta.</li>
              <li>Registros de acceso y actividad (logs de auditoría).</li>
            </ul>
          </section>
          <hr className='legal-divider' />

          {/* 6 */}
          <section className='legal-section' id='s6'>
            <h2>6. Finalidades del tratamiento</h2>
            <p>Los datos personales tratados por Ki Conversations se utilizan exclusivamente para las siguientes finalidades:</p>
            <ul>
              <li>Enviar mensajes de confirmación de citas, recordatorios y comunicaciones operativas a pacientes a través de WhatsApp Business, utilizando la cuenta WhatsApp Business de cada clínica cliente.</li>
              <li>Recibir y registrar las respuestas de pacientes a dichos mensajes.</li>
              <li>Actualizar el estado de cada cita en el tablero de control del coordinador de la clínica.</li>
              <li>Generar estadísticas operativas de efectividad de las comunicaciones (tasas de entrega, lectura, confirmación, cancelación) para beneficio de la clínica cliente.</li>
              <li>Permitir al personal administrativo de la clínica consultar el historial de comunicaciones con cada paciente.</li>
              <li>Cumplir obligaciones legales, contractuales y regulatorias aplicables.</li>
              <li>Atender consultas, quejas y reclamos relacionados con el tratamiento de datos.</li>
            </ul>
            <div className='legal-callout-warning'>
              Ki no utiliza los datos de pacientes para fines publicitarios, comerciales ni de mercadeo propios.
              Ki no vende, alquila ni comercializa datos personales de pacientes bajo ninguna circunstancia.
            </div>
          </section>
          <hr className='legal-divider' />

          {/* 7 */}
          <section className='legal-section' id='s7'>
            <h2>7. Uso de WhatsApp Business Platform</h2>
            <p>Ki Conversations utiliza la plataforma WhatsApp Business de Meta Platforms, Inc. como canal de comunicación entre las clínicas clientes y sus pacientes. Los siguientes aspectos son relevantes:</p>
            <ul>
              <li>Para cada clínica cliente, Ki opera la cuenta WhatsApp Business propia de la clínica. El número telefónico y la cuenta de WhatsApp Business Account (WABA) son propiedad de la clínica, y Ki actúa como operador técnico autorizado.</li>
              <li>Los mensajes enviados a pacientes se originan desde el número de WhatsApp Business de la clínica, no desde un número de Ki. Los pacientes interactúan con su clínica, no con Ki.</li>
              <li>La información intercambiada por este canal es procesada tanto por Ki como por Meta Platforms, Inc. en su calidad de operador de la plataforma WhatsApp Business.</li>
              <li>El uso de WhatsApp por parte de los pacientes está sujeto a los Términos de Servicio y la Política de Privacidad de WhatsApp, disponibles en <a href='https://www.whatsapp.com/legal/privacy-policy' target='_blank' rel='noreferrer'>https://www.whatsapp.com/legal/privacy-policy</a>. Ki recomienda a los pacientes consultar dichos términos.</li>
              <li>Meta Platforms, Inc. puede procesar los datos intercambiados a través de WhatsApp en servidores ubicados fuera de Colombia, principalmente en Estados Unidos e Irlanda, conforme a su propia infraestructura global.</li>
            </ul>
          </section>
          <hr className='legal-divider' />

          {/* 8 */}
          <section className='legal-section' id='s8'>
            <h2>8. Sub-encargados y transferencias internacionales</h2>

            <h3>8.1. Sub-encargados</h3>
            <p>Ki utiliza los siguientes proveedores de servicios tecnológicos que actúan como sub-encargados del tratamiento:</p>
            <ul>
              <li><strong>Meta Platforms, Inc.</strong> (Estados Unidos / Irlanda) – operador de la plataforma WhatsApp Business utilizada para el envío y recepción de mensajes.</li>
              <li><strong>Salesforce, Inc. / Heroku</strong> (Estados Unidos) – proveedor de infraestructura de cómputo y aplicación donde se ejecuta el backend de Ki Conversations.</li>
              <li><strong>MongoDB, Inc. (MongoDB Atlas)</strong> (Estados Unidos) – proveedor de base de datos gestionada donde se almacena la información operativa de citas, mensajes y usuarios.</li>
            </ul>
            <p>
              Ki selecciona únicamente sub-encargados que ofrecen garantías contractuales y técnicas equivalentes a las
              exigidas por la legislación colombiana y que adoptan estándares reconocidos internacionalmente en materia
              de seguridad de la información. En todo caso, de existir cambios futuros en los sub-encargados, estos
              cumplirán las mismas características de confidencialidad a las enunciadas, sin que sea necesaria la
              modificación de la presente política.
            </p>

            <h3>8.2. Transferencias internacionales</h3>
            <p>
              Como se indica en la sección anterior, algunos datos personales de pacientes son procesados fuera del
              territorio colombiano, principalmente en los Estados Unidos de América. Estas transferencias se realizan al amparo de:
            </p>
            <ul>
              <li>Cláusulas contractuales con cada sub-encargado que garantizan niveles de protección equivalentes a los exigidos por la Ley 1581 de 2012.</li>
              <li>La autorización otorgada por la clínica cliente en el contrato de prestación de servicios con Ki.</li>
              <li>Medidas técnicas de seguridad que protegen los datos durante la transferencia y el almacenamiento.</li>
            </ul>
            <div className='legal-callout'>
              Al utilizar Ki Conversations, las clínicas clientes y, en consecuencia, los Titulares cuya información
              es tratada por cuenta de dichas clínicas, aceptan la realización de estas transferencias internacionales
              necesarias para la prestación del servicio.
            </div>
          </section>
          <hr className='legal-divider' />

          {/* 9 */}
          <section className='legal-section' id='s9'>
            <h2>9. Seguridad de la información</h2>
            <p>
              Ki implementa medidas técnicas, administrativas y organizativas orientadas a proteger los datos personales
              contra adulteración, pérdida, consulta, uso o acceso no autorizado o fraudulento. Entre estas medidas se incluyen:
            </p>
            <ul>
              <li>Cifrado de datos en tránsito mediante protocolos TLS.</li>
              <li>Cifrado de credenciales sensibles en reposo.</li>
              <li>Aislamiento lógico de datos por clínica cliente: cada coordinador accede únicamente a los datos de su propia clínica.</li>
              <li>Controles de acceso basados en roles.</li>
              <li>Registros de auditoría de acceso y acciones relevantes.</li>
              <li>Procedimientos de respuesta ante incidentes de seguridad.</li>
              <li>Capacitación del equipo de Ki sobre manejo responsable de datos personales y obligaciones bajo la Ley 1581 de 2012.</li>
            </ul>
            <p>
              A pesar de estas medidas, ningún sistema puede garantizar seguridad absoluta. En caso de que Ki tenga
              conocimiento de un incidente de seguridad que afecte datos personales de pacientes, notificará sin
              demora indebida a la clínica cliente correspondiente, quien en su calidad de Responsable evaluará la
              notificación a los Titulares y a la Superintendencia de Industria y Comercio, según corresponda.
            </p>
          </section>
          <hr className='legal-divider' />

          {/* 10 */}
          <section className='legal-section' id='s10'>
            <h2>10. Retención de datos</h2>
            <p>Ki aplica los siguientes períodos de retención, salvo que un requerimiento legal, contractual o de autoridad competente exija un plazo distinto:</p>
            <ul>
              <li><strong>Contenido y metadatos de mensajes de WhatsApp</strong> (texto, archivos adjuntos, estados de entrega): 24 meses contados desde la fecha de la última interacción del mensaje. Transcurrido este período, los mensajes son eliminados o anonimizados.</li>
              <li><strong>Datos de citas</strong> (nombre del paciente, teléfono, fecha, estado, profesional asignado): durante toda la vigencia del contrato entre Ki y la clínica cliente, más cinco (5) años adicionales posteriores a la terminación del contrato, con el fin de atender obligaciones médico-legales y de trazabilidad propias del sector salud.</li>
              <li><strong>Datos agregados o anonimizados</strong> (estadísticas operativas sin identificadores de pacientes): sin límite de retención, dado que no constituyen datos personales.</li>
              <li><strong>Datos de coordinadores y personal administrativo:</strong> durante la vigencia de la relación entre el coordinador y la clínica cliente, más un (1) año posterior a la desactivación de la cuenta.</li>
            </ul>
            <p>
              Al finalizar la relación contractual con una clínica cliente, Ki entregará a la clínica los datos de sus
              pacientes que esta solicite en un formato estructurado y, una vez confirmada la entrega, procederá a su
              eliminación de los sistemas activos de Ki dentro de los plazos mencionados.
            </p>
          </section>
          <hr className='legal-divider' />

          {/* 11 */}
          <section className='legal-section' id='s11'>
            <h2>11. Derechos de los Titulares</h2>
            <p>Los Titulares de datos personales tienen los siguientes derechos, de conformidad con la Ley 1581 de 2012:</p>
            <ul>
              <li>Conocer, actualizar y rectificar sus datos personales.</li>
              <li>Solicitar prueba de la autorización otorgada.</li>
              <li>Ser informados sobre el uso que se ha dado a sus datos.</li>
              <li>Presentar quejas ante la Superintendencia de Industria y Comercio por infracciones a la normatividad vigente.</li>
              <li>Revocar la autorización y/o solicitar la supresión de sus datos, cuando proceda.</li>
              <li>Acceder de forma gratuita a sus datos personales.</li>
            </ul>

            <h3>11.1. Ejercicio de derechos frente a Ki Conversations</h3>
            <p>
              Dado que Ki actúa como Encargado del Tratamiento y cada clínica cliente es el Responsable, las
              solicitudes relacionadas con derechos de Titulares deben dirigirse preferentemente a la clínica donde
              el paciente recibe atención, ya que es la clínica quien determina las finalidades del tratamiento y
              mantiene la relación directa con el paciente.
            </p>
            <p>
              No obstante lo anterior, los Titulares pueden dirigirse directamente a Ki escribiendo a{' '}
              <a href='mailto:soporte@kiplanner.com'>soporte@kiplanner.com</a>. Ki recibirá la solicitud, la
              canalizará a la clínica cliente correspondiente y colaborará con dicha clínica en su atención dentro
              de los plazos legales.
            </p>
            <p>Las solicitudes deben incluir:</p>
            <ul>
              <li>Nombre completo del Titular.</li>
              <li>Documento de identidad o mecanismo de verificación de identidad.</li>
              <li>Clínica donde recibe o recibió atención.</li>
              <li>Descripción clara de la solicitud.</li>
              <li>Datos de contacto para la respuesta.</li>
            </ul>
            <div className='legal-callout'>
              Los plazos de respuesta son los establecidos en la Ley 1581 de 2012: hasta <strong>diez (10) días hábiles</strong> para
              consultas y hasta <strong>quince (15) días hábiles</strong> para reclamos, prorrogables una sola vez por igual término
              cuando sea necesario.
            </div>
          </section>
          <hr className='legal-divider' />

          {/* 12 */}
          <section className='legal-section' id='s12'>
            <h2>12. Tratamiento de datos sensibles de salud</h2>
            <p>Como se indica en la sección 5, Ki Conversations limita estrictamente el tratamiento de información de salud. Ki no recolecta ni tiene acceso a:</p>
            <ul>
              <li>Historia clínica.</li>
              <li>Diagnósticos médicos o códigos CIE-10.</li>
              <li>Resultados de exámenes, laboratorios o imágenes diagnósticas.</li>
              <li>Prescripciones médicas o medicamentos recetados.</li>
              <li>Información genética o biométrica.</li>
              <li>Datos relativos a la vida sexual u orientación sexual.</li>
            </ul>
            <p>
              La única información con posible connotación de salud que Ki procesa es el tipo general de especialidad o
              procedimiento al que corresponde la cita (por ejemplo: "fisioterapia", "consulta odontológica",
              "endocrinología"), dato necesario para que el paciente identifique inequívocamente la cita a confirmar.
              Esta información es tratada con las medidas de seguridad reforzadas aplicables a datos sensibles y su
              tratamiento es aceptado por los usuarios en los términos de esta política.
            </p>
          </section>
          <hr className='legal-divider' />

          {/* 13 */}
          <section className='legal-section' id='s13'>
            <h2>13. Menores de edad</h2>
            <p>Ki reconoce y respeta los derechos prevalentes de niños, niñas y adolescentes. En el contexto de Ki Conversations, cuando un paciente es menor de edad:</p>
            <ul>
              <li>La autorización para el tratamiento de sus datos debe ser otorgada por sus representantes legales.</li>
              <li>La clínica cliente, como Responsable, es quien garantiza que esta autorización se haya obtenido previamente.</li>
              <li>Las comunicaciones por WhatsApp se dirigen al número telefónico registrado en la clínica, generalmente el del representante legal.</li>
            </ul>
            <p>Ki colabora con las clínicas clientes para asegurar que el tratamiento de datos de menores cumpla con las protecciones reforzadas exigidas por la normatividad colombiana.</p>
          </section>
          <hr className='legal-divider' />

          {/* 14 */}
          <section className='legal-section' id='s14'>
            <h2>14. Cookies y tecnologías similares</h2>
            <p>
              La plataforma web de Ki Conversations (<a href='https://cliente.kiplanner.com' target='_blank' rel='noreferrer'>https://cliente.kiplanner.com</a>) utiliza
              cookies técnicas y de sesión estrictamente necesarias para el funcionamiento de la aplicación,
              incluyendo la autenticación de usuarios y la preservación de la sesión. Ki no utiliza cookies de
              publicidad ni de perfilamiento con fines comerciales en Ki Conversations.
            </p>
          </section>
          <hr className='legal-divider' />

          {/* 15 */}
          <section className='legal-section' id='s15'>
            <h2>15. Modificaciones a esta Política</h2>
            <p>
              Ki se reserva el derecho de modificar esta Política en cualquier momento. Las modificaciones materiales
              serán comunicadas a las clínicas clientes con al menos <strong>quince (15) días hábiles</strong> de antelación a su
              entrada en vigencia, a través de los canales habituales de comunicación y mediante publicación en{' '}
              <a href='https://cliente.kiplanner.com/legal' target='_blank' rel='noreferrer'>
                https://cliente.kiplanner.com/legal
              </a> o en
              <a href='https://www.kiplanner.com/legal/conversations/politica-de-privacidad' target='_blank' rel='noreferrer'>
                https://www.kiplanner.com/legal/conversations/politica-de-privacidad
              </a>.
            </p>
            <p>
              Las clínicas clientes son responsables de comunicar a sus pacientes, cuando corresponda, las
              modificaciones relevantes que afecten el tratamiento de sus datos.
            </p>
          </section>
          <hr className='legal-divider' />

          {/* 16 */}
          <section className='legal-section' id='s16'>
            <h2>16. Contacto y atención de solicitudes</h2>
            <p>
              Para cualquier consulta, solicitud, queja o reclamo relacionado con el tratamiento de datos personales
              en Ki Conversations, los Titulares, clínicas clientes y terceros interesados pueden contactar a:
            </p>
            <div className='legal-contact-card'>
              <p><strong>Ki Planner S.A.S.</strong></p>
              <p>NIT: 901753233-9</p>
              <p>Representante legal: Manuela García Hoyos</p>
              <p>Dirección: Carrera 17 No. 2 sur – 10, Medellín, Antioquia, Colombia</p>
              <p>Correo electrónico: <a href='mailto:soporte@kiplanner.com'>soporte@kiplanner.com</a></p>
              <p>Teléfono: <a href='tel:+573216439960'>+57 321 643 9960</a></p>
            </div>
          </section>
          <hr className='legal-divider' />

          {/* 17 */}
          <section className='legal-section' id='s17'>
            <h2>17. Vigencia y ley aplicable</h2>
            <p>
              Esta Política de Privacidad y Tratamiento de Datos Personales de Ki Conversations entra en vigencia a
              partir del <strong>22 de abril de 2026</strong>. Las bases de datos que contienen información de Titulares tratada por
              Ki Conversations tienen una vigencia determinada por los períodos de retención descritos en la sección
              10, prorrogables cuando subsista una obligación legal o contractual.
            </p>
            <p>
              Esta Política se rige por las leyes de la República de Colombia. Cualquier controversia derivada de su
              interpretación o aplicación se someterá a la jurisdicción de los jueces y tribunales competentes de
              Medellín, Antioquia, Colombia.
            </p>
          </section>

          <p className='legal-end-note'>— Fin del documento —</p>

        </main>
      </div>

      <Footer />
    </div>
  )
}

export default Legal
